import { Injectable } from '@angular/core';
import { CrudService } from './crud.service';
import type { CreatePaymentPayload, Payment, PaymentMetrics, PaymentDueMetrics, UpdatePaymentPayload, PaymentEvolution, PaymentTodayIndicators, TotalPaymentsMetrics } from '../interfaces/payment';
import { map, type Observable } from 'rxjs';
import { buildHttpParams, extractCollection } from '../shared/utils';
import { environment } from '../../environment/environment';
import { ApiResponse } from '../interfaces/application';
import dayjs from 'dayjs';

@Injectable({
  providedIn: 'root'
})
export class PaymentService extends CrudService<Payment, CreatePaymentPayload, UpdatePaymentPayload> {

  constructor() {
    super('payments');
  }

  public override post(data: CreatePaymentPayload): Observable<ApiResponse<Payment>> {
    const payload = this._convertPostToFormdata(data);
    return this._http.post<ApiResponse<Payment>>(`${environment.api}/${this.path}`, payload);
  }

  public override put(id: string, data: UpdatePaymentPayload): Observable<ApiResponse<Payment>> {
    const payload = this._convertPostToFormdata(data);
    return this._http.post<ApiResponse<Payment>>(`${environment.api}/${this.path}/${id}`, payload, { params: { _method: 'PUT' } });
  }

  public topFive(filters: any): Observable<ApiResponse<Payment[]>> {
    const params = buildHttpParams(filters);
    return this._http.get<any>(`${environment.api}/payments/top-five`, { params }).pipe(
      map((response) => ({
        ...response,
        data: extractCollection<Payment>(response?.data),
      }) as ApiResponse<Payment[]>)
    );
  }

  public totalPaymentsMetrics(filters?: any): Observable<ApiResponse<TotalPaymentsMetrics>> {
    const params = buildHttpParams(filters);
    return this._http.get<ApiResponse<TotalPaymentsMetrics>>(`${environment.api}/payments/total-payments-metrics`, { params });
  }

  public metrics(filters?: any): Observable<ApiResponse<PaymentMetrics>> {
    const params = buildHttpParams(filters);
    return this._http.get<ApiResponse<PaymentMetrics>>(`${environment.api}/payments/metrics`, { params });
  }

  public dueMetrics(filters?: any): Observable<ApiResponse<PaymentDueMetrics>> {
    const params = buildHttpParams(filters);
    return this._http.get<ApiResponse<PaymentDueMetrics>>(`${environment.api}/payments/due-metrics`, { params });
  }

  public todayIndicators(filters?: any): Observable<ApiResponse<PaymentTodayIndicators>> {
    const params = buildHttpParams(filters);
    return this._http.get<ApiResponse<PaymentTodayIndicators>>(`${environment.api}/payments/today-indicators`, { params });
  }

  public paymentsEvolution(filters?: any): Observable<ApiResponse<PaymentEvolution>> {
    const params = buildHttpParams(filters);
    return this._http.get<ApiResponse<PaymentEvolution>>(`${environment.api}/payments/payments-evolution`, { params });
  }

  public byCategory(filters?: any): Observable<ApiResponse<any>> {
    const params = buildHttpParams(filters);
    return this._http.get<ApiResponse<any>>(`${environment.api}/payments/by-category`, { params });
  }

  private _convertPostToFormdata(payload: CreatePaymentPayload|UpdatePaymentPayload): FormData {
    const formdata = new FormData();

    payload.contract_id && formdata.append('contract_id', payload.contract_id);
    payload.receiver_id && formdata.append('receiver_id', payload.receiver_id);
    payload.category_id && formdata.append('category_id', payload.category_id);
    payload.subcategory_id && formdata.append('subcategory_id', payload.subcategory_id);
    payload.nfse_key && formdata.append('nfse_key', payload.nfse_key);
    payload.title && formdata.append('title', payload.title);
    payload.due_date && formdata.append('due_date', dayjs(payload.due_date).format('YYYY-MM-DD'));
    payload.issue_at && formdata.append('issue_at', dayjs(payload.issue_at).format('YYYY-MM-DD'));
    payload.value && formdata.append('value', String(payload.value));
    payload.reference_month && formdata.append('reference_month', payload.reference_month);
    payload.type && formdata.append('type', payload.type);
    payload.invoice && formdata.append('invoice', payload.invoice);
    payload.force_duplicate && formdata.append('force_duplicate', payload.force_duplicate ? '1' : '0');

    payload.docs?.forEach(doc => formdata.append('docs[]', doc));

    return formdata;
  }

}
